import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Icon, Label, Loader } from 'semantic-ui-react';
import { fetchCharacterContext, selectContextByChar } from '../../slices/litGroupCastSlice';

/**
 * CharacterLinesPanel — a character's actual dialogue, read-only.
 *
 * ─── ⭐ WHY IT EXISTS ────────────────────────────────────────────────────────────────────────
 *
 * The Casting Studio is where a character's GENDER is corrected, and gender is what Auto-cast pins
 * a voice on. But the studio showed no dialogue: the row carries counts, an archetype and a
 * synopsis, and none of those answer the question an operator is actually asking — *is this a man
 * or a woman, and does the text say so?* The answer was two tabs away in the Green Room, so the
 * decision was being made away from the evidence, or not made at all.
 *
 * ─── ⚠️ WHY THIS IS A SECOND COMPONENT AND NOT THE GREEN ROOM'S PANEL ───────────────────────
 *
 * The Green Room renders the same data, and ideally there would be one component. Its panel is
 * wired through and through to REASSIGNMENT — per-line target/neighbour buttons, an inline speaker
 * dropdown over the whole cast, an undo receipt, and the in-flight state for all three — none of
 * which belongs in a reading panel, and all of which would have to become render props to share it.
 * That refactor touches the tool the operator is using right now, mid-run, to merge a live cast.
 *
 * ⛔ So the duplication is DELIBERATE and it is the presentational half only: both call the same
 * thunk, read the same slice, and page the same way. Unifying them — this component taking an
 * optional line-extras render prop, the Green Room passing its reassign affordances — is the
 * follow-up, and is safe to do when no cast is mid-merge.
 *
 * ─── ⚠️ THE EFFECT IS A GUARDED FETCH, NOT A BARE ONE ───────────────────────────────────────
 *
 * It fires only when the panel has no entry, or when the entry was built for a different search
 * term. Without that guard it refetches on every render, because the fulfilled action replaces the
 * entry and thus changes the dependency. The pending reducer writes `{ loading: true }` first,
 * which is what makes the guard false on the very next run — the same self-limiting shape the
 * Green Room uses, and it matters more here because React StrictMode invokes effects twice in
 * development.
 */
const CharacterLinesPanel = ({ litGroupUuid, characterUuid, q = '', maxChars = 240 }) => {
  const dispatch = useDispatch();
  const contextByChar = useSelector(selectContextByChar);
  const entry = contextByChar[characterUuid];

  useEffect(() => {
    if (!litGroupUuid || !characterUuid) return;
    if (!entry || (!entry.loading && (entry.q || '') !== q)) {
      dispatch(fetchCharacterContext({ litGroupUuid, characterUuid, q }));
    }
  }, [dispatch, litGroupUuid, characterUuid, q, entry]);

  const panelStyle = {
    marginTop: '0.3em',
    paddingLeft: '0.85em',
    borderLeft: '3px solid #e6e6e6',
  };

  if (!entry || entry.loading) {
    return (
      <div style={panelStyle}>
        <Loader active inline size="tiny" /> <span style={{ color: '#999' }}>loading lines…</span>
      </div>
    );
  }
  if (entry.error) {
    return <div style={panelStyle}><span style={{ color: '#9f3a38' }}>Couldn&apos;t load lines: {entry.error}</span></div>;
  }
  if (!entry.passages.length) {
    // ⚠️ Says WHICH of the two silences this is. "No lines" on a character with a line count reads
    // as a failure; this one is a real answer — the name is in the cast because it was attributed,
    // and nothing is attributed to it now.
    return <div style={panelStyle}><span style={{ color: '#999' }}>No dialogue attributed to this name.</span></div>;
  }

  return (
    <div style={panelStyle}>
      {entry.passages.map((p, pi) => (
        <div key={pi} style={{ marginBottom: '0.8em' }}>
          {p.bookTitle && (
            /* ⭐ Book AND chapter (2026-10-09). The label used to name the book only, so an
               operator confirming an identity knew which volume and not where in it — and had to
               go and search the Render view for a line this panel was already showing. */
            <Label size="mini" style={{ marginBottom: '0.3em' }} title="book and chapter">
              {p.bookTitle}{p.chapter != null ? ` · ch ${p.chapter}` : ''}
            </Label>
          )}
          {(p.lines || []).map((l, li) => {
            const text = String(l.text || l.content || '');
            const clipped = text.length > maxChars ? `${text.slice(0, maxChars)}…` : text;
            return (
              <div key={li} style={{
                margin: '0.2em 0',
                lineHeight: 1.4,
                /* The line the panel was opened FOR is tinted; its neighbours are context. Same
                   treatment as the Green Room, so the two panels read as one idea. */
                background: l.isTarget ? '#fff8e1' : 'transparent',
                borderLeft: l.isTarget ? '3px solid #f2c200' : '3px solid transparent',
                paddingLeft: '0.5em',
              }}>
                <span style={{
                  color: l.isTarget ? '#8a6d00' : '#777',
                  fontSize: '0.85em',
                  fontWeight: l.isTarget ? 700 : 400,
                }}>
                  {(l.speaker || '—')}:{' '}
                </span>
                <span style={{ color: l.isTarget ? '#5c4a00' : '#333' }}>{clipped}</span>
              </div>
            );
          })}
        </div>
      ))}

      {entry.total > entry.passages.length && (
        <div style={{ color: '#999', fontSize: '0.85em', marginTop: '0.3em' }}>
          showing {entry.passages.length} of {entry.total} occurrences
          <Button
            size="mini" compact basic
            style={{ marginLeft: '0.6em', padding: '0.2em 0.5em' }}
            onClick={() => dispatch(fetchCharacterContext({
              litGroupUuid,
              characterUuid,
              q,
              samples: Math.min(entry.passages.length + 12, 60),
            }))}
            title="Load more of this character's lines"
          >
            <Icon name="plus" /> show more
          </Button>
        </div>
      )}
    </div>
  );
};

export default CharacterLinesPanel;
